import React from 'react';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import { useLanguage } from '../lib/i18n';

type TrendProps = {
  symbol: string;
  shortTerm: {
    direction: 'up' | 'down' | 'neutral';
    prediction: string;
    confidence: number;
  };
  midTerm: {
    direction: 'up' | 'down' | 'neutral';
    prediction: string;
    confidence: number;
  };
  longTerm: {
    direction: 'up' | 'down' | 'neutral';
    prediction: string;
    confidence: number;
  };
};

export default function TrendAnalysis({ symbol, shortTerm, midTerm, longTerm }: TrendProps) {
  const { t } = useLanguage();

  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="h-5 w-5 text-green-400" />;
      case 'down':
        return <TrendingDown className="h-5 w-5 text-red-400" />;
      default:
        return <ArrowRight className="h-5 w-5 text-yellow-400" />;
    }
  };

  const getTrendColor = (direction: string) => {
    switch (direction) {
      case 'up':
        return 'text-green-400';
      case 'down':
        return 'text-red-400';
      default:
        return 'text-yellow-400';
    }
  };

  const renderTrendCard = (title: string, trend: typeof shortTerm) => (
    <div className="bg-black/20 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-300">{title}</h4>
        {getTrendIcon(trend.direction)}
      </div>
      <p className={`text-lg font-semibold ${getTrendColor(trend.direction)}`}>
        {trend.prediction}
      </p>
      <p className="text-sm text-gray-400 mt-1">
        {t('aiAnalysis.trends.confidence')}: {trend.confidence}%
      </p>
    </div>
  );

  return (
    <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {renderTrendCard(t('aiAnalysis.trends.shortTerm'), shortTerm)}
        {renderTrendCard(t('aiAnalysis.trends.midTerm'), midTerm)}
        {renderTrendCard(t('aiAnalysis.trends.longTerm'), longTerm)}
      </div>
    </div>
  );
}
